import produce from 'immer';
import { humanizePageName } from '@alltrails/shared/utils/pageStringHelpers';
import { getItemInList, humanizeListId, humanizeType, FAVORITE_MAGIC_ID } from '@alltrails/shared/utils/listHelpers';
import { logTrailSaveCompleted, ListContentType } from 'utils/trail_analytics_helpers';
import { ServerCommunicationUtil } from '../utils/server_communication_util';
import { addToList, removeFromList } from '../utils/requests/list_requests';

const LIST_ITEMS = 'listItems';
const USER_LIST_ITEMS = 'userListItems';
const ADDED = 'Added';
const REMOVED = 'Removed';
// reducer name
export const LISTS = 'lists';
// actions
export const ADD_ITEM_TO_LIST_ITEMS = 'ADD_ITEM_TO_LIST_ITEMS';
export const ADD_ITEM_TO_USER_LIST_ITEMS = 'ADD_ITEM_TO_USER_LIST_ITEMS';
export const REMOVE_ITEM_FROM_LIST_ITEMS = 'REMOVE_ITEM_FROM_LIST_ITEMS';
export const REMOVE_ITEM_FROM_USER_LIST_ITEMS = 'REMOVE_ITEM_FROM_USER_LIST_ITEMS';
export const ADD_CREATED_LIST = 'ADD_CREATED_LIST';
export const SET_LISTS_AND_LIST_ITEMS = 'SET_LISTS_AND_LIST_ITEMS';
export const UPDATE_LIST_ORDER = 'UPDATE_LIST_ORDER';

/**
 * addItemToList
 *
 * dispatches a state update with a listItem that has been returned by the server.
 * if the list "belongsToCurrentUser", we need to make an update to the list that a user is viewing.
 *
 * @param {{ listId: number, type: string, objectId: number, id: number, order: number }} - listItem (it returns more, but this is what we need in order to work with our lists)
 * @param {boolean} belongsToCurrentUser
 *
 * it will always dispatch ADD_ITEM_TO_USER_LIST_ITEMS
 * depending if a user is viewing their own list it will dispatch ADD_ITEM_TO_USER_LIST_ITEMS
 *
 */
export const addItemToList = (listItem, belongsToCurrentUser = false) => {
  return dispatch => {
    if (belongsToCurrentUser) {
      dispatch({ type: ADD_ITEM_TO_USER_LIST_ITEMS, payload: { listItem, action: 'add' } });
    }

    dispatch({ type: ADD_ITEM_TO_LIST_ITEMS, payload: { listItem } });
  };
};

/**
 * removeItemFromList
 *
 * dispatches a state update with a deleted listItem that has been returned by the server.
 * if the list "belongsToCurrentUser", we need to make an update to the list that a user is viewing.
 *
 * @param {{ listId: number, type: string, objectId: number, id: number, order: number }} - listItem (it returns more, but this is what we need in order to work with our lists)
 * @param {boolean} belongsToCurrentUser
 *
 * it will always dispatch REMOVE_ITEM_FROM_LIST_ITEMS
 * depending if a user is viewing their own list it will dispatch REMOVE_ITEM_FROM_USER_LIST_ITEMS
 *
 */
export const removeItemFromList = (listItem, belongsToCurrentUser) => {
  return dispatch => {
    if (belongsToCurrentUser) {
      dispatch({ type: REMOVE_ITEM_FROM_USER_LIST_ITEMS, payload: { listItem, action: 'remove' } });
    }

    dispatch({ type: REMOVE_ITEM_FROM_LIST_ITEMS, payload: { listItem } });
  };
};

/**
 * notifyGa
 *
 * sends off tracking information depending on whether or not an item was deleted or added
 *
 * @param {number} listId
 * @param {string} type -- ex. trail, map, activity
 * @param {string} page
 * @param {string} itemState --ex added or removed
 *
 * we are using the "humanize" functions to keep the tracking consistent with how it is now
 *
 */
const notifyGa = (listId, type, page, itemState) => {
  ServerCommunicationUtil.handleGaqPush(['_trackEvent', humanizePageName(page), `${humanizeListId(listId)} ${itemState}`, humanizeType(type)]);
};

/**
 * logAmplitudeEvent
 *
 * logs an event to amplitude with trail/list meta data & whether an item was deleted or added
 *
 * @param {string} trailId
 * @param {number} listId
 * @param {boolean} isNewList
 * @param {string} page
 * @param {string} itemState --ex added or removed
 */
const logAmplitudeEvent = (trailId, listId, isNewList, page, itemState) => {
  if (page === 'TRAIL_PAGE') {
    const isFavoritesList = listId.toString() === FAVORITE_MAGIC_ID;
    logTrailSaveCompleted(trailId, listId, isFavoritesList ? ListContentType.Favorites : ListContentType.Custom, isNewList, itemState === REMOVED);
  }
};

/**
 * addListItem
 *
 * responsible for making the addToList request, tracking the result and then adding it to the reducer
 * For user experience, we add the listItem to state and if there is an error then we remove it
 *
 * @param {number} userId
 * @param {string} type
 * @param {number} objectId
 * @param {number} listId
 * @param {string} page
 * @param {boolean} belongsToCurrentUser
 *
 */
export const addListItem = (userId, type, objectId, listId, page, belongsToCurrentUser) => {
  return (dispatch, getState) => {
    dispatch(addItemToList({ listId, type, objectId }, belongsToCurrentUser));
    return addToList(listId, type, objectId, userId)
      .then(addedListItem => {
        const {
          lists: { newLists }
        } = getState();
        dispatch(addItemToList(addedListItem, belongsToCurrentUser));
        notifyGa(listId, type, page, ADDED);
        logAmplitudeEvent(objectId, listId, Object.prototype.hasOwnProperty.call(newLists, listId), page, ADDED);
      })
      .catch(err => {
        dispatch(removeItemFromList({ listId, type, objectId }, belongsToCurrentUser));
        console.error('THUNK ACTION addListItem error', err);
      });
  };
};

/**
 * removeFromListItem
 *
 * responsible for making the removeFromList request, tracking the result and then removing it to the reducer
 * For user experience, we remove the listItem to state and if there is an error then we add it back
 *
 * @param {number} userId
 * @param {string} type
 * @param {number} itemId this represents the ID that connects a object (trail, map, recording) to a list
 * @param {number} listId
 * @param {number} objectId
 * @param {number} order
 * @param {string} page
 * @param {boolean} belongsToCurrentUser
 *
 */
export const removeFromListItem = (userId, type, itemId, listId, objectId, order, page, belongsToCurrentUser) => {
  return (dispatch, getState) => {
    dispatch(removeItemFromList({ type, listId, objectId }, belongsToCurrentUser));
    if (!itemId) return;

    return removeFromList(listId, itemId, userId)
      .then(() => {
        const {
          lists: { newLists }
        } = getState();
        notifyGa(listId, type, page, REMOVED);
        logAmplitudeEvent(objectId, listId, Object.prototype.hasOwnProperty.call(newLists, listId), page, REMOVED);
      })
      .catch(err => {
        dispatch(addItemToList({ listId, type, id: itemId, objectId, order }, belongsToCurrentUser));
        console.error('THUNK ACTION removeFromListItem error', err);
      });
  };
};

/**
 * toggleListItem
 *
 * this method checks to see if a listItem is included in the list at hand and then either removes it if it
 * does exist or adds it to a list if it does not already exist
 *
 * @param {number} userId
 * @param {number} listId
 * @param {string} type
 * @param {number} objectId
 * @param {string} page
 * @param {boolean} belongsToCurrentUser
 *
 */
export const toggleListItem = (userId, listId, type, objectId, page) => {
  return (dispatch, getState) => {
    const {
      lists: { listItems, belongsToCurrentUser }
    } = getState();

    const item = getItemInList(type, objectId, listItems[listId], page);
    return item
      ? dispatch(removeFromListItem(userId, type, item.id, listId, objectId, item.order, page, belongsToCurrentUser))
      : dispatch(addListItem(userId, type, objectId, listId, page, belongsToCurrentUser));
  };
};

/**
 * addCreatedList
 *
 * adds a new created list
 *
 * @param {{ id: number, order: number, title: string }}
 *
 */
export const addCreatedList = ({ id, order, title }) => {
  return { type: ADD_CREATED_LIST, payload: { id, order, title } };
};

/**
 * setListsAndListItems
 *
 * A big part of this thunk is to be able to keep the TrailPage and the explore view in coordination
 * since the trail page is rendered in an Iframe that actually hits a separate instance of
 * Alltrails. Not ideal, but in the meantime before we get rid of the iframe we have to have a way to keep
 * the two lists in sync
 *
 * @param {{ lists: {object} , listItems: {object} }}
 *
 */
export const setListsAndListItems = ({ lists, listItems }) => {
  return { type: SET_LISTS_AND_LIST_ITEMS, payload: { lists, listItems } };
};

/**
 * updateListOrder
 *
 * Updates a list with newly ordered items
 *
 * @param {string[]} listItems - updated list items
 * @param {string} listId - id of the list to update
 *
 */
export const updateListOrder = (listItems, listId) => {
  return { type: UPDATE_LIST_ORDER, payload: { listItems, listId } };
};

export const initialListState = {
  listItems: {},
  lists: [],
  userListItems: {},
  belongsToCurrentUser: false,
  latestListAction: null
};

// disabled default case because Immer automatically returns a default case
/* eslint-disable default-case, no-unused-expressions */

/**
 * handleAddToListOfExistingType
 *
 * if a type of content, ie a trail, map, activity is available in a list
 *
 * @param draftState -- immer draft state, allows us to mutabily change things immutability
 * @param {{listId: number, type: string, objectId: number, id: number, order: number, objectId}} listItem - this is a list that is added
 * @param {string} listItemsType - decides which state to push the new listItem into. users or their own list.
 *
 */
const handleAddToListExistingType = (draftState, { listId, type, id, order, objectId }, listItemsType = LIST_ITEMS) => {
  draftState[listItemsType][listId][type][objectId] = {
    id,
    order
  };
};

/**
 * handleAddToListNewType
 *
 * if a type of content, ie a trail, map, activity does not exist on a list, we need to update the object so it's type
 * is there.
 *
 * @param draftState -- immer draft state, allows us to mutabily change things immutability
 * @param {{listId: number, type: string, objectId: number, id: number, order: number}} listItem - this is a list that is added
 * @param {string} listItemsType - decides which state to push the new listItem into. users or their own list.
 *
 */
const handleAddToListNewType = (draftState, { listId, type, id, order, objectId }, listItemsType = LIST_ITEMS) => {
  draftState[listItemsType][listId][type] = {
    [objectId]: {
      id,
      order
    }
  };
};

/**
 * handleRemoveFromList
 *
 * removes a listItem from a list and if it has an empty type then we remove the type as well.
 *
 * @param draftState -- immer draft state, allows us to mutabily change things immutability
 * @param {{listId: number, type: string, objectId: number, id: number, order: number}} listItem - this is a list that is added
 * @param {string} action - this can be either added or removed. This is used to integrate with our existing system of updating
 * a filter on explore pages when a user makes an update to their own list.
 * @param {string} listItemsType - decides which state to push the new listItem into. users or their own list.
 *
 */
const handleRemoveFromList = (draftState, { listItem, action }, listItemsType = LIST_ITEMS) => {
  const list = draftState[listItemsType][listItem.listId];
  delete list[listItem.type][listItem.objectId];

  if (Object.keys(list[listItem.type]).length === 0) {
    delete list[listItem.type];
  }

  if (action) {
    draftState.latestListAction = { listItem, action };
  }
};

/**
 * handleAddToList
 *
 * makes the determination if the type for the listItem already exists ie trail, map, activity
 * and adds it to an existing type or creates the type to bucket it in.
 *
 * @param draftState -- immer draft state, allows us to mutably change things immutability
 * @param {{listId: number, type: string, objectId: number, id: number, order: number}} listItem - this is a list that is added
 * @param {string} action - this can be either added or removed. This is used to integrate with our existing system of updating
 * a filter on explore pages when a user makes an update to their own list.
 * @param {string} listItemsType - decides which state to push the new listItem into. users or their own list.
 *
 */
const handleAddToList = (draftState, { listItem, action }, listItemsType = LIST_ITEMS) => {
  Object.prototype.hasOwnProperty.call(draftState[listItemsType][listItem.listId], listItem.type)
    ? handleAddToListExistingType(draftState, listItem, listItemsType)
    : handleAddToListNewType(draftState, listItem, listItemsType);

  if (action) {
    draftState.latestListAction = { listItem, action };
  }
};

/**
 * handleListReordering
 *
 * converts an array of list items into the format that comes from the server
 * and updates the listItems state variable to persist list order changes on the client
 *
 * @param draftState -- immer draft state, allows us to mutably change things immutability
 * @param {string[]} listItems - updated list items
 * @param {string} listId - id of the list to update
 *
 */
const handleListReordering = (draftState, { listItems, listId }) => {
  const listInfo = {};
  listItems.forEach(({ id, objectId, type, order }) => {
    if (!Object.prototype.hasOwnProperty.call(listInfo, type)) {
      listInfo[type] = {};
    }
    listInfo[type][objectId] = { id, order };
  });

  draftState.listItems[listId] = listInfo;
};

const lists = produce((draftState, action) => {
  switch (action.type) {
    case ADD_ITEM_TO_LIST_ITEMS:
      handleAddToList(draftState, action.payload);
      break;
    case ADD_ITEM_TO_USER_LIST_ITEMS:
      handleAddToList(draftState, action.payload, USER_LIST_ITEMS);
      break;
    case REMOVE_ITEM_FROM_LIST_ITEMS:
      handleRemoveFromList(draftState, action.payload);
      break;
    case REMOVE_ITEM_FROM_USER_LIST_ITEMS:
      handleRemoveFromList(draftState, action.payload, USER_LIST_ITEMS);
      break;
    case SET_LISTS_AND_LIST_ITEMS:
      draftState.lists = action.payload.lists;
      draftState.listItems = action.payload.listItems;
      break;
    case ADD_CREATED_LIST:
      draftState.lists.push({ id: action.payload.id, order: action.payload.order, title: action.payload.title });
      draftState.listItems[action.payload.id] = {};
      draftState.newLists[action.payload.id] = true;
      if (draftState.belongsToCurrentUser) {
        draftState.userListItems[action.payload.id] = {};
      }
      break;
    case UPDATE_LIST_ORDER:
      handleListReordering(draftState, action.payload);
      break;
  }
}, initialListState);

export default lists;
